import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
// 引入翻譯管道
import { TranslatePipe } from '../../pipes/translate.pipe';
// 引入 i18n 服務
import { I18nService } from '../../services/i18n.service';
import { EnvironmentService } from '../../services/enviroment.service';
import { environment } from '../../services/global-environment';
import { environment as localenv } from '../../environments/environment';
import { LoginService } from '../../services/login/login.service';
import { LogService } from '../../services/Logging.service';
import { AuthService } from '../../services/auth.service';
import { MfaVerifyComponent } from '../../conponents/security/mfa-verify/mfa-verify.component';
import { MfaSetupComponent } from '../../conponents/security/mfa-setup/mfa-setup.component';
import { LanguageSwitcherComponent } from '../../conponents/shared/language-switcher/language-switcher.component';

@Component({
  selector: 'app-login',
  standalone: true,
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule, // 導入 RouterModule
    TranslatePipe, // 添加翻譯管道
    MfaVerifyComponent, // 添加 MFA 驗證元件
    MfaSetupComponent, // 添加 MFA 設定元件
    LanguageSwitcherComponent, // 添加語言切換元件
  ],
})
export class LoginComponent implements OnInit {
  loginForm: FormGroup;

  login_title = '';
  logoUrl = '';
  copyright = '';
  version = '';
  clientId = '';
  companyname = '';
  multilanguage = false;

  isSubmitting = false;
  showMfaVerification = false;
  showMfaSetup = false;
  currentLoginData: any = null;
  currentLoginResult: any = null;

  // 翻譯鍵值
  readonly TITLE_KEY = 'login.title';
  readonly ID_KEY = 'login.account';
  readonly PW_KEY = 'login.password';
  readonly LOGIN_BTN_KEY = 'login.button';
  readonly FORGET_PW_KEY = 'login.forget_password';

  // Login 頁面的文字顯示
  title_text = 'login.title';
  id_text = 'login.account';
  pw_text = 'login.password';
  login_text = 'login.button';
  forget_text = 'login.forget_password';

  constructor(
    private fb: FormBuilder,
    private router: Router, // 注入 Router
    private loginService: LoginService, // 使用自定義的 LoginService
    private LogService: LogService,
    private authService: AuthService,
    private i18nService: I18nService // 注入 i18n 服務
  ) {
    this.loginForm = this.fb.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
    });
  }

  ngOnInit() {
    // 載入設定檔
    this.logoUrl = environment.Login_logoUrl;
    this.copyright = environment.copyright;
    this.login_title = environment.Login_title;
    this.version = environment.version;
    this.clientId = environment.clientId;
    this.companyname = environment.companyname;

    this.multilanguage = environment.multi_language;


    // 使用翻譯鍵值，不再從環境中獲取
    this.title_text = this.TITLE_KEY;
    this.id_text = this.ID_KEY;
    this.pw_text = this.PW_KEY;
    this.login_text = this.LOGIN_BTN_KEY;
    this.forget_text = this.FORGET_PW_KEY;

    // 語言變更時更新頁面內容
    this.i18nService.currentLang.subscribe((lang) => {
      this.updateTranslations();
    });
  }

  // 更新翻譯方法
  updateTranslations(): void {
    // 使用翻譯管道時此處不需要額外操作
    // 如果有需要動態處理的文字可在這裡更新
  }

  async onSubmit() {
    if (this.loginForm.valid) {
      this.isSubmitting = true; // 按鈕進入 disabled 狀態
      const loginData = this.loginForm.value;

      try {
        const result = await this.loginService.login(this.clientId, loginData);

        if (result.success && result.username && result.modules && result.companyid) {
          // Store login data and result for potential MFA verification
          this.currentLoginData = loginData;
          this.currentLoginResult = result;

          localStorage.setItem('username', result.username );

          // 檢查是否為測試環境，如果是測試環境則跳過 MFA 驗證
          if (!localenv.production) {
            console.log('測試環境：跳過 MFA 驗證');
            this.completeLogin(result);
            return;
          }

          // Check if MFA is required for this user
          this.authService.checkMfaRequired(result.username, result.companyid).subscribe({
            next: (mfaResponse) => {
              console.log('MFA Response:', mfaResponse);

              // 處理 API 回應的資料結構 - 檢查是否有 data 屬性
              const mfaData = mfaResponse.data || mfaResponse;

              if (mfaData.mfaRequired) {
                if (mfaData.mfaSetupRequired) {
                  // User needs to set up MFA first
                  this.showMfaSetup = true;
                  this.isSubmitting = false;
                } else {
                  // User has MFA enabled, show verification screen
                  this.showMfaVerification = true;
                  this.isSubmitting = false;
                }
              } else {
                // Complete login without MFA
                this.completeLogin(result);
              }
            },
            error: (error) => {
              console.error('Error checking MFA requirement:', error);
              // If we can't check MFA requirement, proceed without MFA
              this.completeLogin(result);
            }
          });

        } else {
          this.LogService.Loginlog(
            this.clientId,
            'Login',
            loginData.username,
            'Invalid username or password',
            'WARNING'
          );

          alert('Invalid username or password');
          this.isSubmitting = false;
        }
      } catch (error) {
        let errorMessage = 'Unknown error occurred';

        if (error instanceof Error) {
          errorMessage = error.message;
        }

        this.LogService.Loginlog(
          this.clientId,
          'Error',
          'unknown',
          'Login failed due to server error',
          'ERROR'
        );

        console.error('Login failed:', error);
        alert('An error occurred during login.');
        this.isSubmitting = false;
      }
    }
  }

  onMfaVerified(verified: boolean) {
    if (verified && this.currentLoginResult) {
      this.completeLogin(this.currentLoginResult);
    } else {
      this.onMfaCancelled();
    }
  }

  onMfaCancelled() {
    this.showMfaVerification = false;
    this.showMfaSetup = false;
    this.currentLoginData = null;
    this.currentLoginResult = null;
    this.isSubmitting = false;
    this.loginForm.reset();
  }

  onMfaSetupCompleted(completed: boolean) {
    if (completed && this.currentLoginResult) {
      // MFA setup completed, now show verification screen
      this.showMfaSetup = false;
      this.showMfaVerification = true;
    } else {
      this.onMfaSetupCancelled();
    }
  }

  onMfaSetupCancelled() {
    this.showMfaSetup = false;
    this.currentLoginData = null;
    this.currentLoginResult = null;
    this.isSubmitting = false;
    this.loginForm.reset();
  }

  private completeLogin(result: any) {
    this.authService.storeToken(
      'JWTToken_Mock',
      this.clientId,
      result.username,
      result.modules,
      result.id || ""
    );

    // Load user MFA status
    this.authService.loadUserMfaStatus(result.username, result.companyid);

    // ✅ 記錄登入日誌
    this.LogService.Loginlog(
      this.clientId,
      'Login',
      result.username,
      'User logged in successfully',
      'INFO'
    );

    // ✅ 導向主頁
    this.router.navigate(['/lobby']);

    this.isSubmitting = false;
    this.showMfaVerification = false;
    this.showMfaSetup = false;
    this.currentLoginData = null;
    this.currentLoginResult = null;
  }
}
